/**
 * A link's preview card data, derived from its URL alone — no Open Graph fetch (Constraint
 * bd2bf57a: a link title is derived from its URL path). Pure and client-safe: the writer's link
 * block (`discussions-writer.ts` re-exports it) and the thread's reply cards (`ReplyBody`) both
 * read it.
 */

/** DealHunt's link limits (`DISCUSSION_LIMITS`, contract §2): `link_title_max`, `url_max`. */
export const LINK_LIMITS = {
  linkTitleChars: 120,
  urlChars: 2000,
} as const

export interface LinkPreview {
  /** The URL as it is sent (https, normalised by `URL`). */
  url: string
  /** Hostname without `www.`, lowercase — the display uppercases it. */
  domain: string
  /** From the last path segment ("sony-wh-1000xm6-review" → "Sony Wh 1000xm6 Review"); null
   *  for a bare domain, which the preview then names by its domain. */
  title: string | null
}

function titleFromSegment(segment: string): string | null {
  let text = segment
  try {
    text = decodeURIComponent(segment)
  } catch {
    // A malformed escape: keep the raw segment.
  }
  text = text
    // Escapes can decode to control or bidi-override characters (`%00`, `%E2%80%AE`): never in a title.
    .replace(/[\p{Cc}\u202A-\u202E\u2066-\u2069]/gu, ' ')
    .replace(/\.[a-z0-9]{2,5}$/i, '')
    .replace(/[-_+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text || /^\d+$/.test(text)) {
    return null
  }
  const titled = text.replace(
    /(^|\s)(\p{L})/gu,
    (_m, sp: string, c: string) => sp + c.toUpperCase()
  )
  return titled.slice(0, LINK_LIMITS.linkTitleChars)
}

/**
 * The preview card's data once the URL is valid: https only (a bare `example.com/page` is read
 * as https, an explicit `http:` is not accepted), a host with a dot. Null otherwise.
 */
export function linkPreview(raw: string): LinkPreview | null {
  const value = raw.trim()
  if (!value || /\s/.test(value)) {
    return null
  }
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(value)
  let parsed: URL
  try {
    parsed = new URL(hasScheme ? value : `https://${value}`)
  } catch {
    return null
  }
  if (
    parsed.protocol !== 'https:' ||
    parsed.username ||
    parsed.password ||
    // Normalising (IDN → punycode, percent-encoding) can lengthen it past DealHunt's 2 000.
    parsed.href.length > LINK_LIMITS.urlChars
  ) {
    return null
  }
  const host = parsed.hostname.toLowerCase()
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host)) {
    return null
  }
  const segments = parsed.pathname.split('/').filter(Boolean)
  const last = segments.length ? segments[segments.length - 1] : ''
  return {
    url: parsed.href,
    domain: host.replace(/^www\./, ''),
    title: last ? titleFromSegment(last) : null,
  }
}
